import {inject} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {RoleEnum} from "@app/core/models/java.model";
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from "@app/authentication";

/**
 * Guard pour protéger les routes basées sur les rôles utilisateur
 */
export const RoleGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  try {
    // Vérification de l'authentification
    if (!authService.isTokenValid()) {
      handleUnauthorized(router, toastr, 'Veuillez vous connecter pour accéder à cette page');
      return false;
    }

    // Récupération du rôle requis depuis les données de route
    const requiredRole = route.data['role'] as RoleEnum;
    if (requiredRole === undefined) {
      console.error('RoleGuard: Aucun rôle spécifié dans les données de route');
      handleUnauthorized(router, toastr, 'Erreur de configuration');
      return false;
    }

    // Vérification du rôle de l'utilisateur
    const userRole = authService.getRole();
    if (!userRole) {
      handleUnauthorized(router, toastr, "Vous n'avez pas les permissions nécessaires");
      return false;
    }

    // Vérifie si l'utilisateur a le rôle requis
    if (userRole !== requiredRole) {
      handleUnauthorized(router, toastr, "Accès refusé - Permissions insuffisantes");
      return false;
    }

    return true;

  } catch (error) {
    console.error('Erreur dans le RoleGuard:', error);
    handleUnauthorized(router, toastr, 'Une erreur est survenue');
    return false;
  }
};

/**
 * Gère les cas d'accès non autorisés
 */
const handleUnauthorized = (
  router: Router,
  toastr: ToastrService,
  message: string
): void => {
  toastr.error(message);

  const currentUrl = router.url;
  router.navigate(['/authentication/login'], {
    queryParams: {returnUrl: currentUrl}
  });
};

/**
 * Guard pour protéger les routes nécessitant plusieurs rôles
 */
export const MultiRoleGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  try {
    if (!authService.isTokenValid()) {
      handleUnauthorized(router, toastr, 'Veuillez vous connecter pour accéder à cette page');
      return false;
    }

    const requiredRoles = route.data['roles'] as RoleEnum[];
    if (!requiredRoles?.length) {
      console.error('MultiRoleGuard: Aucun rôle spécifié dans les données de route');
      handleUnauthorized(router, toastr, 'Erreur de configuration');
      return false;
    }

    const userRole = authService.getRole();
    if (!userRole) {
      handleUnauthorized(router, toastr, "Vous n'avez pas les permissions nécessaires");
      return false;
    }

    if (!requiredRoles.includes(userRole as RoleEnum)) {
      handleUnauthorized(router, toastr, "Accès refusé - Permissions insuffisantes");
      return false;
    }

    return true;

  } catch (error) {
    console.error('Erreur dans le MultiRoleGuard:', error);
    handleUnauthorized(router, toastr, 'Une erreur est survenue');
    return false;
  }
};

/**
 * Décorateur pour vérifier les rôles dans les composants
 */
export function HasRole(role: RoleEnum) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any[]) {
      const authService = inject(AuthenticationService);
      const toastr = inject(ToastrService);

      if (!authService.isTokenValid() || authService.getRole() !== role) {
        toastr.error("Vous n'avez pas les permissions nécessaires");
        return;
      }

      return originalMethod.apply(this, args);
    };

    return descriptor;
  };
}
